import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '@/features/auth';

export function AccountSettings() {
  const { user } = useAuth();
  const { changePassword, changeEmail, enableMfa, disableMfa, isLoading, error, clearError } =
    useSettings(null);

  // Change Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Change Email state
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);

  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(user?.mfaEnabled ?? false);
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaPassword, setMfaPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) return;
    if (newPassword.length < 8) return;

    try {
      await changePassword({ currentPassword, newPassword, confirmPassword });
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      // error handled by hook
    }
  };

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setEmailSuccess(false);

    try {
      await changeEmail({ newEmail, password: emailPassword });
      setEmailSuccess(true);
      setNewEmail('');
      setEmailPassword('');
    } catch {
      // error handled by hook
    }
  };

  const handleMfaToggle = async () => {
    if (mfaEnabled) {
      // Disable MFA
      setMfaDialogOpen(true);
    } else {
      // Enable MFA
      setMfaDialogOpen(true);
    }
  };

  const handleMfaConfirm = async () => {
    try {
      if (mfaEnabled) {
        await disableMfa({ password: mfaPassword });
        setMfaEnabled(false);
      } else {
        await enableMfa({ password: mfaPassword, code: mfaCode || undefined });
        setMfaEnabled(true);
      }
      setMfaDialogOpen(false);
      setMfaPassword('');
      setMfaCode('');
    } catch {
      // error handled by hook
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Change Password */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Alterar Senha
      </Typography>
      {passwordSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Senha alterada com sucesso.
        </Alert>
      )}
      <Box component="form" onSubmit={handleChangePassword} sx={{ mb: 4, maxWidth: 400 }}>
        <TextField
          fullWidth
          type="password"
          label="Senha atual"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          type="password"
          label="Nova senha"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          helperText="Minimo 8 caracteres, 1 maiuscula, 1 minuscula, 1 número"
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          type="password"
          label="Confirmar nova senha"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={confirmPassword.length > 0 && newPassword !== confirmPassword}
          helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? 'Senhas não conferem' : ''}
          sx={{ mb: 2 }}
          required
        />
        <Button type="submit" variant="contained" disabled={isLoading}>
          {isLoading ? <CircularProgress size={20} /> : 'Alterar senha'}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Change Email */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Alterar Email
      </Typography>
      {emailSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Email de verificação enviado para o novo endereço.
        </Alert>
      )}
      <Box component="form" onSubmit={handleChangeEmail} sx={{ mb: 4, maxWidth: 400 }}>
        <TextField
          fullWidth
          type="email"
          label="Novo email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          type="password"
          label="Senha para confirmar"
          value={emailPassword}
          onChange={(e) => setEmailPassword(e.target.value)}
          sx={{ mb: 2 }}
          required
        />
        <Button type="submit" variant="contained" disabled={isLoading}>
          {isLoading ? <CircularProgress size={20} /> : 'Alterar email'}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* MFA */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Autenticação Multifator (MFA)
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Switch checked={mfaEnabled} onChange={handleMfaToggle} />
        <Typography>
          {mfaEnabled ? 'MFA ativado' : 'MFA desativado'}
        </Typography>
      </Box>

      {/* MFA Dialog */}
      <Dialog open={mfaDialogOpen} onClose={() => setMfaDialogOpen(false)}>
        <DialogTitle>
          {mfaEnabled ? 'Desativar MFA' : 'Ativar MFA'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="Senha"
            value={mfaPassword}
            onChange={(e) => setMfaPassword(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            required
          />
          {!mfaEnabled && (
            <TextField
              fullWidth
              label="Codigo TOTP"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              placeholder="000000"
              inputProps={{ maxLength: 6 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMfaDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleMfaConfirm} variant="contained" disabled={isLoading}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
